import type {
  GroupColorSlot,
  GroupId,
  GroupsSlice,
  OrderTicketPrefs,
  SymbolGroup,
  TicketsSlice,
  WidgetsSlice,
  WorkspaceDateRange,
} from "./workspace.types";

// ---------------------------------------------------------------------------
// Ordered constant tuples (source of truth for id ↔ color mapping)
// ---------------------------------------------------------------------------

export const GROUP_IDS = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
] as const satisfies readonly GroupId[];

export const GROUP_COLOR_SLOTS = [
  "blue",
  "green",
  "yellow",
  "orange",
  "red",
  "purple",
  "pink",
  "teal",
] as const satisfies readonly GroupColorSlot[];

// ---------------------------------------------------------------------------
// Symbol normalization
// ---------------------------------------------------------------------------

/**
 * Trims surrounding whitespace and uppercases a raw symbol string.
 * Preserves internal delimiters (e.g. "/" in "BTC/USD").
 */
export const normalizeWorkspaceSymbol = (symbol: string): string =>
  symbol.trim().toUpperCase();

// ---------------------------------------------------------------------------
// Group id allocation
// ---------------------------------------------------------------------------

/**
 * Returns the smallest letter `GroupId` not present in `ids`, or `undefined`
 * when all twenty-six slots are occupied.
 */
export const getNextGroupId = (
  ids: readonly GroupId[],
): GroupId | undefined => {
  const occupied = new Set(ids);
  return GROUP_IDS.find((id) => !occupied.has(id));
};

// ---------------------------------------------------------------------------
// Color slot mapping
// ---------------------------------------------------------------------------

/**
 * Returns the default `GroupColorSlot` for a letter id (cycles the eight
 * palette slots by letter index). Throws on any id outside A–Z.
 */
export const getDefaultGroupColor = (id: GroupId): GroupColorSlot => {
  const index = GROUP_IDS.indexOf(id);
  if (index < 0) {
    throw new Error(`Invalid Layout Labs group id: ${id}`);
  }
  return GROUP_COLOR_SLOTS[index % GROUP_COLOR_SLOTS.length];
};

// ---------------------------------------------------------------------------
// Group letter / label
// ---------------------------------------------------------------------------

/** Group ids are letters; identity helper for call sites. */
export const getGroupLetter = (id: GroupId): string => id;

/** Maps letter A–Z (case-insensitive) → GroupId. */
export const getGroupIdFromLetter = (letter: string): GroupId | undefined => {
  if (letter.length !== 1) {
    return undefined;
  }
  const id = letter.toUpperCase() as GroupId;
  return GROUP_IDS.find((candidate) => candidate === id);
};

/**
 * Display label for a group: `"A"` or `"A - My Group"` when a custom name
 * is set.
 */
export const getGroupLabel = (
  group: Pick<SymbolGroup, "id" | "name">,
): string =>
  group.name
    ? `${getGroupLetter(group.id)} - ${group.name}`
    : getGroupLetter(group.id);

// ---------------------------------------------------------------------------
// Effective symbol resolution (publisher-beats-consumer precedence)
// ---------------------------------------------------------------------------

type WorkspaceState = GroupsSlice & WidgetsSlice & TicketsSlice;

/**
 * Resolves the effective display/routing symbol for a widget.
 *
 * Precedence:
 *  1. Assigned group's `linkState.symbol` (normalized) — publisher wins.
 *  2. Chart or Watchlist widget's own `localSymbol`.
 *  3. Order ticket's `localSymbol` from the tickets slice.
 *  4. `undefined` — widget has no group and no local symbol.
 */
export const getEffectiveWidgetSymbol = (
  state: WorkspaceState,
  widgetId: string,
): string | undefined => {
  const widget = state.widgets[widgetId];
  if (!widget) {
    return undefined;
  }

  if (widget.groupId !== null) {
    const group = state.groups[widget.groupId];
    const raw = group?.linkState.symbol;
    return raw ? normalizeWorkspaceSymbol(raw) : undefined;
  }

  if (widget.kind === "chart" || widget.kind === "watchlist") {
    return widget.localSymbol || undefined;
  }

  if (widget.kind === "orderTicket") {
    const ticket = state.tickets[widgetId];
    return ticket?.localSymbol || undefined;
  }

  return undefined;
};

/**
 * Resolves the symbol a widget's chrome title names.
 *
 * An order ticket holding legs is an order for their underlying, which is not
 * the group's symbol when they were routed from an overridden options chain. A
 * ticket holds one underlying at a time, so the first leg speaks for all of
 * them. Every other case, an empty ticket included, names the effective symbol.
 */
export const getWidgetTitleSymbol = (
  state: WorkspaceState,
  widgetId: string,
): string | undefined => {
  if (state.widgets[widgetId]?.kind === "orderTicket") {
    const underlying =
      state.tickets[widgetId]?.legs[0]?.contract.underlying_symbol;
    if (underlying) {
      return normalizeWorkspaceSymbol(underlying);
    }
  }
  return getEffectiveWidgetSymbol(state, widgetId);
};

// ---------------------------------------------------------------------------
// Order-ticket preferences
// ---------------------------------------------------------------------------

export const DEFAULT_ORDER_TICKET_PREFS: OrderTicketPrefs = {
  reviewEnabled: true,
  reviewPresentation: "inline",
};

/** Fills any missing pref with its default — the store may hold none at all. */
export const resolveOrderTicketPrefs = (
  prefs: Partial<OrderTicketPrefs> | undefined,
): OrderTicketPrefs => ({
  reviewEnabled:
    prefs?.reviewEnabled ?? DEFAULT_ORDER_TICKET_PREFS.reviewEnabled,
  reviewPresentation:
    prefs?.reviewPresentation ?? DEFAULT_ORDER_TICKET_PREFS.reviewPresentation,
});

/**
 * Resolves the effective date range for a widget from its assigned group.
 * Ungrouped widgets never inherit a range.
 */
export const getEffectiveWidgetDateRange = (
  state: WorkspaceState,
  widgetId: string,
): WorkspaceDateRange | undefined => {
  const widget = state.widgets[widgetId];
  if (!widget || widget.groupId === null) {
    return undefined;
  }
  return state.groups[widget.groupId]?.linkState.dateRange;
};
